import React, { useState, useEffect, useRef, useCallback } from "react";
// import { Navigate } from "react-router-dom";
import Grid from "@mui/material/Grid2";

import { 
  Campaign, 
  CampaignChar, 
  Character,
} from "@dndModels";
import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import DisplayObject from "@dndComponents/display/DisplayObject";
import DandDButtonBox from "@dndComponents/DandDButtonBoxSimple";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import CharacterSheetParent from "@dndComponents/character/CharacterSheetParent";
import LoadingBox from "@components/input/LoadingBox";

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

interface Props {
  c_char: CampaignChar;
  campaign: Campaign;
  // updateCampaign: () => void;
  onUpdated: () => void; // This will trigger CampaignCharactersInput to see changes that are outside this control.
}

export default function CampaignCharacterInput({ c_char, campaign, onUpdated }: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [viewing, setViewing] = useState(false);
  // const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const load = useCallback( async () => {
    // console.log(c_char);
    if (!c_char.char || !c_char.char.owner) {
      await api.loadCChars({ campaign, include_players: true, include_chars: true });
    }
    setLoading(false);
  }, [c_char]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched, load]);

  const updateCharacter = async (updates?: Partial<Character>) => {
    const user = api.user;
    if (user && updates) {
      setProcessing(true);
      const res = await char_util.getCharacter({ id: c_char.char_id, campaign });
      if (res) {
        Object.assign(res, updates);
        await api.updateCharacter(res, ["main"], true);
        const res2 = await char_util.getCharacterFromCookie(c_char.char_id);
        const cookie_char = res2;
        if (cookie_char !== null) {
          Object.assign(cookie_char, updates);
          await api.updateCharacter(cookie_char, ["main"]);
        }
        const old_char = campaign.characters.find(o => o._id === c_char.char_id);
        if (old_char) {
          Object.assign(old_char, updates);
        } 
      }
      // updateCampaign();
      setProcessing(false);
    }
  };

  const updateCChar = async (updates?: Partial<CampaignChar>) => {
    const user = api.user;
    if (user && updates) {
      setProcessing(true);
      Object.assign(c_char, updates);
      await api.upsertObject("campaign_char", c_char);
      onUpdated();
      setProcessing(false);
    }
  };

  const copyCharacter = async () => {
    // if (char_util.ready) {
    const char = c_char.char;
    const user = api.user;
    if (user && char) {
      setProcessing(true);
      const res = await char_util.getCharacter({ id: char._id, campaign });
      if (res) {
        const new_obj = res.clone(true);
        new_obj.name = new_obj.name + " (Copy)";
        new_obj._id = "";
        new_obj.owner_id = user._id;
        const res2 = await api.createCharacter(new_obj);
        if (res2.body && !res2.body.error) {
          // Now add the c_char for it.
          const new_c_char = new CampaignChar();
          new_c_char.campaign_id = campaign._id;
          new_c_char.campaign = campaign;
          new_c_char.char = new_obj;
          new_c_char.char_id = res2.body.id;
          const res3 = await api.upsertObject("campaign_char", new_c_char);
          console.log(res3);
          console.log(new_c_char);
          campaign.c_chars.push(new_c_char);
          onUpdated();
          setProcessing(false);
          // setNavigateTo(`/character/${res2.body.id}`);
        }
      }
      setProcessing(false);
    }
  }

  const renderExtras = () => {
    const char = c_char.char;
    if (char) {
      console.log(char);
      return (
        <ModalOrDrawer
          open={viewing}
          width={ window.innerWidth * 0.9 }
          onClose={() => { setViewing(false); }}>
          <CharacterSheetParent
            id={char._id}
            char={char}
            campaign={campaign}
          />
        </ModalOrDrawer>
      );
    }
    return null;
  }

  // if (navigateTo !== null) {
  //   return <Navigate to={navigateTo} />;
  // } else 
  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    const char = c_char.char;
    if (char) {
      // if (char.name.includes("Copy")) {
        console.log(char);
        console.log(campaign);
      // }
      if (api.user && (campaign.owner_id === api.user._id || campaign.player_ids.includes(api.user._id))) {
        if (char.owner_id === "") {
          return (
            <Grid container spacing={0} direction="row">
              <Grid size={12}>
                <DisplayObject obj={char} />
              </Grid>
              <Grid size={6}>
                <DandDButtonBox name="View" 
                  disabled={ processing } 
                  link_url={`/character/${char._id}`}
                  onClick={() => {
                    setViewing(true);
                  }}
                />
                { renderExtras() }
              </Grid>
              <Grid size={6}>
                <DandDButtonBox name="Claim" 
                  disabled={ processing || !api.online } 
                  onClick={() => {
                    if (api.user) {
                      updateCharacter({ owner_id: api.user._id, owner: api.user });
                    }
                  }}
                />
              </Grid>
              <Grid size={6}>
                <DandDButtonBox name="Copy" 
                  disabled={ processing || !api.online } 
                  onClick={copyCharacter}
                />
              </Grid>
              <Grid size={6}>
                <DandDButtonBox name={ c_char.active ? "Deactivate" : "Activate" }
                  disabled={ processing || !api.online } 
                  onClick={() => {
                    updateCChar({ active: !c_char.active });
                  }}
                />
              </Grid>
            </Grid>
          );
        } else if (api.user._id === char.owner_id || api.user._id === campaign.owner_id) {
          const owner = char.owner;
          // if (char.name.includes("Harmony")) {
          //   console.log(owner);
          //   console.log(char);
          //   console.log(obj.players);
          // }
          return (
            <Grid container spacing={0} direction="row">
              <Grid size={12}>{ char.name }</Grid>
              <Grid size={12} className="small_label gray">
                { char.race_and_class_string } { char.sidekick ? " (Sidekick)" : "" } - { owner?.use_name??"" }
              </Grid>
              <Grid size={6}>
                <DandDButtonBox name="View" 
                  disabled={ processing } 
                  link_url={`/character/${char._id}`}
                  onClick={() => {
                    setViewing(true);
                  }}
                />
                { renderExtras() }
              </Grid>
              <Grid size={6}>
                <DandDButtonBox name="Unassign" 
                  disabled={ processing || !api.online } 
                  onClick={ async () => {
                    await updateCharacter({ owner_id: "", owner: undefined });
                  }}
                />
              </Grid>
              <Grid size={4}>
                <DandDButtonBox name="Copy" 
                  disabled={ processing || !api.online } 
                  onClick={copyCharacter}
                />
              </Grid>
              <Grid size={4}>
                <DandDButtonBox 
                  name="Leave Campaign" 
                  disabled={ processing || !api.online } 
                  onClick={ async () => {
                    await updateCharacter({ old_campaign_id: "", campaign: undefined });
                    setProcessing(true);
                    const char = c_char.char;
                    if (char) {
                      char.c_char = undefined; // Not really necessary because it doesn't get saved anywhere, but it's good housekeeping.
                    }
                    campaign.c_chars = campaign.c_chars.filter(o => o._id !== c_char._id);
                    await api.deleteObject("campaign_char", c_char);
                    setProcessing(false);
                    onUpdated();
                  }}
                />
              </Grid>
              <Grid size={4}>
                <DandDButtonBox name={ c_char.active ? "Deactivate" : "Activate" }
                  disabled={ processing || !api.online } 
                  onClick={ async () => {
                    updateCChar({ active: !c_char.active });
                  }}
                />
              </Grid>
            </Grid>
          );
        } else {
          const owner = campaign.players.find(o => o._id === char.owner_id);
          return (
            <Grid container spacing={0} direction="row">
              <Grid size={12}>{ char.name }</Grid>
              { owner && 
                <Grid size={12}
                  style={{ 
                    fontSize: "8px", 
                    color: "gray",
                  }}>
                  { char.race_and_class_string } { char.sidekick ? " (Sidekick)" : "" } - { owner?.use_name??"" }
                </Grid>
              }
              <Grid size={12}>
                <DandDButtonBox name="View" 
                  disabled={ processing } 
                  link_url={`/character/${char._id}`}
                  onClick={() => {
                    setViewing(true);
                  }}
                />
                { renderExtras() }
              </Grid>
            </Grid>
          );
        }
      } else {
        return (
          <>{ char.name }&nbsp;{ char.race_and_class_string }</>
        );
      }
    } else {
      return <>Character not found.</>
    }
  }
}
